/**
 * customer-app · 网络层（带超时与有限次自动重试的 fetch）
 *
 * 来源：js/api.js（已迁移）。
 *
 * 暴露 API（通过 window.RestaurantApiService 与 OS.registerModule('api', …)）：
 *   - fetchWithTimeoutAndRetry: 通用底层
 *   - pingApi:                  GET 探活，返回 { ok, message }
 *   - postJson:                 POST JSON，返回 { ok, status, data }
 *   - postCustomerOrder:        顾客下单 wrapper
 *   - sendThermalOrderViaPrintLib: 委托给 RestaurantPrintLib
 *   - pingPrintServerHealth:    打印服务 /health 探活
 *
 * 兼容契约（必须保留）：
 *   - `window.RestaurantApiService` 字段名 / 函数签名 / 返回结构 与 js/api.js 完全一致
 *     （app.js 与 ui-controller.js 在用 globalThis.RestaurantApiService.* 调用）。
 *   - `OS.registerModule('api', …)` 当 OS 总线就绪时注册（global-store.js 提供）。
 *   - iOS 12 兼容由 esbuild target=ios12 + supported.optional-catch-binding=false 处理；
 *     本文件可放心用 ES2017+ 语法（async/await/optional chaining/扩展运算符等）。
 */

/* ─────────── 类型定义（导出供后续 customer-app TS 模块复用） ─────────── */

export type FetchKind = 'network' | 'timeout' | '5xx';

export interface FetchRetryOpts {
  timeoutMs?: number;
  maxRetries?: number;
  retryDelayMs?: number;
  retryOn?: FetchKind[];
}

export interface PingOptions {
  timeoutMs?: number;
  maxRetries?: number;
}

export interface PostJsonOptions {
  timeoutMs?: number;
  maxRetries?: number;
  headers?: Record<string, string>;
}

export interface PingResult {
  ok: boolean;
  message: string;
}

export interface PostJsonResult<T = unknown> {
  ok: boolean;
  status: number;
  /** 后端 JSON 解析成功就是 T；解析失败返回原始 text；本层错误返回 { error } */
  data: T | string | { error: string };
}

export interface PostCustomerOrderSkipped {
  ok: false;
  skipped: true;
}

export type PostCustomerOrderResult<T = unknown> = PostJsonResult<T> | PostCustomerOrderSkipped;

export interface PrintLibResultItem {
  printerLogical: string;
  ok: boolean;
  status: number;
  data: unknown;
}

export interface PrintLibResult {
  ok: boolean;
  results: PrintLibResultItem[];
}

interface PrintLib {
  sendOrderToServer: (order: unknown) => Promise<PrintLibResult>;
}

export interface RestaurantApiService {
  fetchWithTimeoutAndRetry: (
    url: string,
    init?: RequestInit | null,
    opt?: FetchRetryOpts,
  ) => Promise<Response>;
  pingApi: (url: string, options?: PingOptions) => Promise<PingResult>;
  postJson: <T = unknown>(
    url: string,
    body: unknown,
    options?: PostJsonOptions,
  ) => Promise<PostJsonResult<T>>;
  postCustomerOrder: <T = unknown>(
    order: unknown,
    apiBaseUrl: string,
  ) => Promise<PostCustomerOrderResult<T>>;
  sendThermalOrderViaPrintLib: (order: unknown) => Promise<PrintLibResult>;
  pingPrintServerHealth: (printBaseUrl: string) => Promise<PingResult>;
}

/* ─────────── 全局 facade 局部断言（不污染 customer-bootstrap 的全局声明） ─────────── */

interface RuntimeGlobal {
  RestaurantApiService?: RestaurantApiService;
  RestaurantPrintLib?: PrintLib;
  OS?: {
    registerModule?: (name: string, ref: unknown) => unknown;
  };
}

const G: RuntimeGlobal = (typeof window !== 'undefined' ? window : globalThis) as RuntimeGlobal;

/* ─────────── 工具 ─────────── */

const sleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

const fetchErrorKind = (err: unknown): '' | 'timeout' | 'network' => {
  if (!err || typeof err !== 'object') return '';
  return (err as { name?: string }).name === 'AbortError' ? 'timeout' : 'network';
};

const shouldRetryFetch = (
  err: unknown,
  res: Response | null,
  opt?: FetchRetryOpts,
): boolean => {
  const retryOn = new Set<FetchKind>(opt?.retryOn ?? ['network', '5xx']);
  const kind = fetchErrorKind(err);
  if (kind && retryOn.has(kind as FetchKind)) return true;
  if (res && res.status >= 500 && res.status <= 599 && retryOn.has('5xx')) return true;
  return false;
};

/* ─────────── 核心：fetch + timeout + retry ─────────── */

const fetchWithTimeoutAndRetry = async (
  url: string,
  init?: RequestInit | null,
  opt?: FetchRetryOpts,
): Promise<Response> => {
  const timeoutMs = opt?.timeoutMs ?? 30000;
  const maxRetries = Math.max(0, opt?.maxRetries ?? 2);
  const retryDelayMs = opt?.retryDelayMs ?? 800;
  const canAbort = typeof AbortController === 'function';

  let lastErr: unknown;
  let attempt = 0;

  while (attempt <= maxRetries) {
    let tid: ReturnType<typeof setTimeout> | undefined;
    try {
      let res: Response;
      if (canAbort) {
        const ctrl = new AbortController();
        tid = setTimeout(() => ctrl.abort(), timeoutMs);
        res = await fetch(url, { ...(init ?? {}), signal: ctrl.signal });
      } else {
        /** 兼容老 WebKit 没有 AbortController：用 Promise.race 模拟超时 */
        const initObj = init ?? {};
        const timeoutErr = new Error('timeout');
        timeoutErr.name = 'AbortError';
        const timeoutP = new Promise<Response>((_, rej) => {
          tid = setTimeout(() => rej(timeoutErr), timeoutMs);
        });
        res = await Promise.race([fetch(url, initObj), timeoutP]);
      }
      if (tid !== undefined) clearTimeout(tid);
      if (!res.ok && shouldRetryFetch(null, res, opt) && attempt < maxRetries) {
        attempt++;
        await sleep(retryDelayMs * attempt);
        continue;
      }
      return res;
    } catch (e) {
      if (tid !== undefined) clearTimeout(tid);
      lastErr = e;
      if (shouldRetryFetch(e, null, opt) && attempt < maxRetries) {
        attempt++;
        await sleep(retryDelayMs * attempt);
        continue;
      }
      throw e;
    }
  }
  throw lastErr ?? new Error('fetch failed');
};

/* ─────────── 高层封装 ─────────── */

const pingApi = (url: string, options?: PingOptions): Promise<PingResult> => {
  const u = String(url ?? '').trim();
  if (!u) return Promise.resolve({ ok: false, message: '请先填写 API 地址' });
  const timeoutMs = options?.timeoutMs ?? 10000;
  const maxRetries = options?.maxRetries ?? 2;
  return fetchWithTimeoutAndRetry(
    u,
    { method: 'GET', mode: 'cors', cache: 'no-store' },
    { timeoutMs, maxRetries, retryOn: ['network', 'timeout', '5xx'] },
  )
    .then((res) => ({ ok: res.ok, message: `HTTP ${res.status} ${res.statusText}` }))
    .catch((e: unknown) => {
      const name = (e as { name?: string } | null)?.name;
      const message = (e as { message?: string } | null)?.message;
      return {
        ok: false,
        message:
          name === 'AbortError'
            ? '请求超时'
            : String(message ?? '请求失败（可能是跨域 CORS 或服务未启动）'),
      };
    });
};

const postJson = <T = unknown>(
  url: string,
  body: unknown,
  options?: PostJsonOptions,
): Promise<PostJsonResult<T>> => {
  const u = String(url ?? '').trim();
  if (!u) return Promise.resolve({ ok: false, status: 0, data: { error: 'URL 为空' } });
  const timeoutMs = options?.timeoutMs ?? 30000;
  const maxRetries = options?.maxRetries ?? 1;
  return fetchWithTimeoutAndRetry(
    u,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(options?.headers ?? {}) },
      body: typeof body === 'string' ? body : JSON.stringify(body),
      mode: 'cors',
    },
    { timeoutMs, maxRetries, retryOn: ['network', 'timeout', '5xx'] },
  )
    .then((res) =>
      res.text().then((text): PostJsonResult<T> => {
        let data: T | string;
        try {
          data = JSON.parse(text) as T;
        } catch {
          data = text;
        }
        return { ok: res.ok, status: res.status, data };
      }),
    )
    .catch((e: unknown): PostJsonResult<T> => {
      const name = (e as { name?: string } | null)?.name;
      const message = (e as { message?: string } | null)?.message;
      return {
        ok: false,
        status: 0,
        data: { error: name === 'AbortError' ? '请求超时' : String(message ?? e) },
      };
    });
};

const postCustomerOrder = <T = unknown>(
  order: unknown,
  apiBaseUrl: string,
): Promise<PostCustomerOrderResult<T>> => {
  const base = String(apiBaseUrl ?? '').trim().replace(/\/$/, '');
  if (!base) return Promise.resolve({ ok: false, skipped: true });
  return postJson<T>(`${base}/api/orders`, order);
};

const sendThermalOrderViaPrintLib = (order: unknown): Promise<PrintLibResult> => {
  const PL = G.RestaurantPrintLib;
  if (!PL || typeof PL.sendOrderToServer !== 'function') {
    return Promise.resolve({
      ok: false,
      results: [
        {
          printerLogical: '',
          ok: false,
          status: 0,
          data: { error: 'RestaurantPrintLib 未加载' },
        },
      ],
    });
  }
  return PL.sendOrderToServer(order);
};

const pingPrintServerHealth = (printBaseUrl: string): Promise<PingResult> => {
  const base = String(printBaseUrl ?? '').trim().replace(/\/$/, '');
  if (!base) return Promise.resolve({ ok: false, message: '打印地址为空' });
  return pingApi(`${base}/health`, { timeoutMs: 8000, maxRetries: 2 });
};

/* ─────────── 暴露 ─────────── */

const service: RestaurantApiService = {
  fetchWithTimeoutAndRetry,
  pingApi,
  postJson,
  postCustomerOrder,
  sendThermalOrderViaPrintLib,
  pingPrintServerHealth,
};

G.RestaurantApiService = service;

if (typeof G.OS?.registerModule === 'function') {
  try {
    G.OS.registerModule('api', service);
  } catch {
    /* OS facade 注册失败不影响业务 */
  }
}
